import {
	Anchor,
	AnchorProps,
	ButtonBase,
	ButtonBaseProps,
	FontIconName,
	Icon,
	IconButton
} from "client/Components";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	Ref,
	KeyboardEvent
} from "react";
import styles from "./ItemLayout.module.less";
import classNames from "classnames";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import { AppColorViewModel } from "client/Api/Apps/common";

export type ItemLayoutProps = {
	trackingId?: string;
	hasNoHover?: boolean;
	ref: Ref<HTMLDivElement>;
	onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
	tabIndex: number;
};

export const ItemLayout = (
	props: PropsWithChildren<ItemLayoutProps>
): ReactElement => {
	const {
		trackingId,
		hasNoHover = false,
		children,
		ref,
		onKeyDown,
		tabIndex
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{ [styles.root_hoverable]: !hasNoHover }
			)}
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={tabIndex}
		>
			{children}
		</div>
	);
};
ItemLayout.displayName = "ItemLayout";

const ItemIconContainer = (props: PropsWithChildren<{}>): ReactElement => {
	const { children } = props;

	return (
		<div className={styles.iconContainer}>
			{children}
		</div>
	);
};
ItemIconContainer.displayName = "ItemLayout.IconContainer";

export type FontIconProps = {
	icon: FontIconName;
};

const FontIcon = (props: FontIconProps): ReactElement => {
	const {
		icon
	} = props;

	return (
		<div
			className={classNames(
				styles.icon,
				styles.icon_font
			)}
		>
			<Icon value={icon} size="large" />
		</div>
	);
};
FontIcon.displayName = "ItemLayout.FontIcon";

export type AppColorIconProps = {
	icon: FontIconName;
	color?: AppColorViewModel;
};

const AppColorIcon = (props: AppColorIconProps): ReactElement => {
	const {
		icon,
		color
	} = props;

	const {
		colorClass,
		colorStyle
	} = getReactColorAttributes(color);

	return (
		<div
			className={classNames(
				styles.icon,
				styles.icon_appColor,
				colorClass
			)}
			style={colorStyle}
		>
			<Icon value={icon} size="large" />
		</div>
	);
};
AppColorIcon.displayName = "ItemLayout.AppColorIcon";

export type InfoWrapperProps = {
	verticalAlignment?: VerticalAlignment;
	trackingId?: string;
};

const InfoWrapper = (props: PropsWithChildren<InfoWrapperProps>): ReactElement => {
	const {
		children,
		verticalAlignment = "top",
		trackingId
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.infoWrapper,
				verticalAlignmentToStyleMap[verticalAlignment]
			)}
		>
			{children}
		</div>
	);
};
InfoWrapper.displayName = "ItemLayout.InfoWrapper";

type VerticalAlignment =
	| "top"
	| "center";

const verticalAlignmentToStyleMap: Record<VerticalAlignment, string | null> = {
	["top"]: null,
	["center"]: styles.infoWrapper_isCenterVertical,
};

export type TitleProps = {
	trackingId?: string;
	rightAddition?: ReactElement;
	title?: string;
};

const Title = (props: PropsWithChildren<TitleProps>): ReactElement => {
	const {
		children,
		title,
		rightAddition,
		trackingId
	} = props;

	return (
		<div className={styles.header}>
			<h4
				{...setTrackingId(trackingId)}
				className={styles.title}
				title={title}
			>
				{children}
			</h4>

			{rightAddition && (
				<div className={styles.rightAddition}>
					{rightAddition}
				</div>
			)}
		</div>
	);
};
Title.displayName = "ItemLayout.Title";

export type SubTitleProps = {
	trackingId?: string;
};

const SubTitle = (props: PropsWithChildren<SubTitleProps>): ReactElement => {
	const {
		children,
		trackingId
	} = props;

	return (
		<span {...setTrackingId(trackingId)}>
			<span
				{...setTrackingId("general-info")}
			>
				{children}
			</span>
		</span>
	);
};
SubTitle.displayName = "ItemLayout.SubTitle";

const Content = (props: PropsWithChildren<{}>): ReactElement => {
	const { children } = props;

	return (
		<div
			{...setTrackingId("content")}
			className={styles.content}>
			{children}
		</div>
	);
};
Content.displayName = "ItemLayout.Content";

const InfoIconWrapper = (props: PropsWithChildren<{}>): ReactElement => {
	const { children } = props;

	return (
		<span className={styles.infoIconWrapper}>
			{children}
		</span>
	);
};
InfoIconWrapper.displayName = "ItemLayout.InfoIconWrapper";

export type LocationButtonProps = Omit<
	ButtonBaseProps,
	"className"
	| "type"
	| "trackingId"
	| "ref"
>;

const LocationButton = (props: LocationButtonProps): ReactElement => {
	return (
		<ButtonBase
			{...props}
			trackingId="open-location"
			className={styles.locationButtonContainer}
			type="button"
		>
			<Icon value="folder-open" size="medium" />
		</ButtonBase>
	);
};
LocationButton.displayName = "ItemLayout.LocationButton";

export type InfoProps = {
	trackingId?: string;
	title?: string;
};

const Info = (props: PropsWithChildren<InfoProps>): ReactElement => {
	const { children, trackingId } = props;

	return (
		<div {...setTrackingId(trackingId)} className={styles.info}>
			{children}
		</div>
	);
};
Info.displayName = "ItemLayout.Info";

export type LinkProps = Omit<AnchorProps, "className" | "ref">;

const Link = (props: LinkProps): ReactElement => {
	const { children, ...other } = props;

	return (
		<Anchor {...other} className={styles.link}>
			{children}
		</Anchor>
	);
};
Link.displayName = "ItemLayout.Link";

const Statistics = (props: PropsWithChildren<InfoProps>): ReactElement => {
	const {
		children,
		trackingId
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.statistics}
		>
			{children}
		</div>
	);
};
Statistics.displayName = "ItemLayout.Statistics";

export type ThumbnailProps = {
	height?: "small" | "default";
	isHeightReserver?: boolean;
};

const Thumbnail = (props: PropsWithChildren<ThumbnailProps>): ReactElement => {
	const {
		height = "default",
		isHeightReserver = false,
		children
	} = props;

	return (
		<div
			className={classNames(
				styles.thumbnail,
				{
					[styles.thumbnail_smallHeight]: height === "small",
					[styles.thumbnail_heightReserver]: isHeightReserver
				}
			)}
		>
			{children}
		</div>
	);
};
Thumbnail.displayName = "ItemLayout.Thumbnail";

export type AdvancedSearchItemLayoutAction = {
	id: string;
	icon: FontIconName;
	onClick: () => void;
};

export type ActionsProps = {
	actions: readonly AdvancedSearchItemLayoutAction[];
};

const Actions = (props: ActionsProps): ReactElement => {
	const {
		actions
	} = props;

	return (
		<div className={styles.actions}>
			{actions.map(action => {
				return (
					<IconButton
						key={action.id}
						trackingId={generateTrackingId("action-button", `|${action.id}`)}
						icon={action.icon}
						onClick={action.onClick}
						bubbleClick={false}
						kind="transparent"
						shape="round"
					/>
				);
			})}
		</div>
	);
};
Actions.displayName = "ItemLayout.Actions";

ItemLayout.IconContainer = ItemIconContainer;
ItemLayout.FontIcon = FontIcon;
ItemLayout.AppColorIcon = AppColorIcon;
ItemLayout.Content = Content;
ItemLayout.InfoWrapper = InfoWrapper;
ItemLayout.Title = Title;
ItemLayout.SubTitle = SubTitle;
ItemLayout.InfoIconWrapper = InfoIconWrapper;
ItemLayout.LocationButton = LocationButton;
ItemLayout.Info = Info;
ItemLayout.Link = Link;
ItemLayout.Statistics = Statistics;
ItemLayout.Thumbnail = Thumbnail;
ItemLayout.Actions = Actions;
